import React, { useEffect, useRef, useState } from "react";
import { useCreateGameTask, useEditGameTask, useGameTasks } from "@seabrookstudios/pitch2table";
import { sanitiseTaskName } from "@seabrookstudios/pitch2table-core";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").TaskId} props.taskId
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
const CompleteImportedTask = ({ gameId, taskId, onComplete, onError }) => {
  const editTask = useEditGameTask(gameId, taskId);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }

    hasStarted.current = true;

    editTask
      .mutateAsync({
        name: undefined,
        order: undefined,
        status: "Done",
      })
      .then(() => {
        onComplete();
      })
      .catch(() => {
        onError();
      });
  }, [gameId, taskId, editTask, onComplete, onError]);

  return null;
};

/**
 * @param {import("../nestifyz/parseExport").NestiFyzProgressChecklistItem} item
 * @returns {string|undefined}
 */
const trimTask = (item) => {
  return sanitiseTaskName(item.item?.trim() || "");
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("../nestifyz/parseExport").NestiFyzProgressChecklistItem} props.item
 * @param {import("@seabrookstudios/pitch2table-core").Task|undefined} props.task
 * @param {() => void} props.onStart
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
const CreateImportedTask = ({ gameId, item, task, onStart, onComplete, onError }) => {
  const createTask = useCreateGameTask(gameId);
  const [createdTaskId, setCreatedTaskId] = useState(
    /** @type {import("@seabrookstudios/pitch2table-core").TaskId|null} */ (null)
  );
  const hasStarted = useRef(false);

  const trimmedItem = trimTask(item);

  useEffect(() => {
    if (hasStarted.current || !item.item) {
      return;
    }

    if (task) {
      hasStarted.current = true;

      if (item.value && task.status !== "Done") {
        setCreatedTaskId(task.id);
        return;
      }

      onComplete();
      return;
    }

    if (!trimmedItem) {
      hasStarted.current = true;
      onComplete();
      return;
    }

    hasStarted.current = true;
    onStart();

    createTask
      .mutateAsync({
        name: trimmedItem,
      })
      .then((task) => {
        if (!task?.id) {
          throw new Error("Task was not created");
        }

        if (item.value) {
          setCreatedTaskId(task.id);
          return;
        }

        onComplete();
      })
      .catch(() => {
        onError();
      });
  }, [gameId, item, task, trimmedItem, createTask, onStart, onComplete, onError]);

  if (!createdTaskId) {
    return null;
  }

  return <CompleteImportedTask gameId={gameId} taskId={createdTaskId} onComplete={onComplete} onError={onError} />;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("../nestifyz/parseExport").NestiFyzProgressChecklistItem[]} props.items
 * @param {import("@seabrookstudios/pitch2table-core").Task[]} props.tasks
 * @param {(index: number) => void} props.onItemStart
 * @param {(index: number) => void} props.onItemComplete
 * @param {(index: number) => void} props.onItemError
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
export const GameImportProcessor = ({
  gameId,
  items,
  tasks,
  onItemStart,
  onItemComplete,
  onItemError,
  onComplete,
  onError,
}) => {
  const [itemIndex, setItemIndex] = useState(0);
  const currentItem = items[itemIndex];

  if (!currentItem?.item) {
    return null;
  }

  const advance = () => {
    if (itemIndex + 1 >= items.length) {
      onComplete();
      return;
    }

    setItemIndex((index) => index + 1);
  };

  return (
    <CreateImportedTask
      key={`${itemIndex}-${currentItem.item}`}
      gameId={gameId}
      item={currentItem}
      task={tasks.find((task) => task.name === trimTask(currentItem))}
      onStart={() => {
        onItemStart(itemIndex);
      }}
      onComplete={() => {
        onItemComplete(itemIndex);
        advance();
      }}
      onError={() => {
        onItemError(itemIndex);
        onError();
      }}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("../nestifyz/parseExport").NestiFyzProgressChecklistItem[]} props.items
 * @param {(index: number) => void} props.onItemStart
 * @param {(index: number) => void} props.onItemComplete
 * @param {(index: number) => void} props.onItemError
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
export const GameImportProgressChecklist = ({
  gameId,
  items,
  onItemStart,
  onItemComplete,
  onItemError,
  onComplete,
  onError,
}) => {
  const { data: tasks = [], isLoading } = useGameTasks(gameId);

  if (isLoading) {
    return null;
  }

  return (
    <GameImportProcessor
      gameId={gameId}
      items={items}
      tasks={tasks}
      onItemStart={onItemStart}
      onItemComplete={onItemComplete}
      onItemError={onItemError}
      onComplete={onComplete}
      onError={onError}
    />
  );
};
